import { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import './Products.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

const EMPTY_VERSION_FORM = { versionNumber: '', color: '', additionalPrice: '0', sizeId: '', compositionId: '' }
const EMPTY_CREATE_FORM  = { title: '', price: '', description: '' }

const Products = () => {
    const { user } = useContext(AuthContext)

    const [products, setProducts]         = useState([])
    const [loading, setLoading]           = useState(true)
    const [selected, setSelected]         = useState(null)   // { product, versions }
    const [detailLoading, setDetailLoading] = useState(false)

    const [showCreate, setShowCreate]     = useState(false)
    const [createForm, setCreateForm]     = useState(EMPTY_CREATE_FORM)
    const [creating, setCreating]         = useState(false)

    const [editMode, setEditMode]         = useState(false)
    const [editForm, setEditForm]         = useState({})
    const [saving, setSaving]             = useState(false)

    const [showVersionForm, setShowVersionForm] = useState(false)
    const [versionForm, setVersionForm]   = useState(EMPTY_VERSION_FORM)
    const [sizes, setSizes]               = useState([])
    const [compositions, setCompositions] = useState([])
    const [creatingVersion, setCreatingVersion] = useState(false)

    const [newSize, setNewSize]           = useState('')
    const [addingSize, setAddingSize]     = useState(false)
    const [addingComp, setAddingComp]     = useState(false)
    const [showNewCompForm, setShowNewCompForm] = useState(false)
    const [newCompForm, setNewCompForm]   = useState({ name: '', materials: [{ materialName: '', proportion: '' }] })

    const [deletingProductId, setDeletingProductId]   = useState(null)
    const [deletingVersionId, setDeletingVersionId]   = useState(null)

    const [publishStatus, setPublishStatus]   = useState(null)  // { published, product }
    const [publishing, setPublishing]         = useState(false)

    const [uploadingImage, setUploadingImage] = useState(false)
    const [uploadProgress, setUploadProgress] = useState('')  // message d'étape

    const loadProducts = async () => {
        setLoading(true)
        try {
            const data = await apiFetch('/product')
            setProducts(data.products || [])
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { loadProducts() }, [])

    const loadSizesAndCompositions = async () => {
        try {
            const [sData, cData] = await Promise.all([
                apiFetch('/size'),
                apiFetch('/composition'),
            ])
            setSizes(sData.sizes || [])
            setCompositions(cData.compositions || [])
        } catch (err) {
            console.error(err)
        }
    }

    const selectProduct = async (id) => {
        setDetailLoading(true)
        setEditMode(false)
        setShowVersionForm(false)
        setPublishStatus(null)
        try {
            const [data, statusData] = await Promise.all([
                apiFetch(`/product/${id}`),
                apiFetch(`/product/${id}/status`),
            ])
            setSelected(data)
            setPublishStatus(statusData)
            setEditForm({
                title: data.product.title,
                price: data.product.price,
                description: data.product.description || '',
            })
        } catch (err) {
            console.error(err)
        } finally {
            setDetailLoading(false)
        }
    }

    const handlePublish = async () => {
        setPublishing(true)
        try {
            await apiFetch(`/product/${selected.product._id}/publish`, { method: 'POST' })
            setPublishStatus({ published: true })
        } catch (err) {
            alert(err.message)
        } finally {
            setPublishing(false)
        }
    }

    const handleUnpublish = async () => {
        setPublishing(true)
        try {
            await apiFetch(`/product/${selected.product._id}/unpublish`, { method: 'POST' })
            setPublishStatus({ published: false })
        } catch (err) {
            alert(err.message)
        } finally {
            setPublishing(false)
        }
    }

    const handleCreate = async (e) => {
        e.preventDefault()
        setCreating(true)
        try {
            const data = await apiFetch('/product', {
                method: 'POST',
                body: JSON.stringify({
                    title: createForm.title,
                    price: parseFloat(createForm.price),
                    description: createForm.description,
                    brand: user?.brand,
                }),
            })
            setProducts(prev => [data.product, ...prev])
            setCreateForm(EMPTY_CREATE_FORM)
            setShowCreate(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setCreating(false)
        }
    }

    const handleSaveEdit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            const data = await apiFetch(`/product/${selected.product._id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    title: editForm.title,
                    price: parseFloat(editForm.price),
                    description: editForm.description,
                }),
            })
            setSelected(prev => ({ ...prev, product: data.product }))
            setProducts(prev => prev.map(p => p._id === data.product._id ? data.product : p))
            setEditMode(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleDeleteProduct = async (id) => {
        if (!window.confirm('Supprimer ce produit et toutes ses versions ?')) return
        setDeletingProductId(id)
        try {
            await apiFetch(`/product/${id}`, { method: 'DELETE' })
            setProducts(prev => prev.filter(p => p._id !== id))
            setSelected(null)
        } catch (err) {
            alert(err.message)
        } finally {
            setDeletingProductId(null)
        }
    }

    const openVersionForm = async () => {
        await loadSizesAndCompositions()
        setVersionForm(EMPTY_VERSION_FORM)
        setNewSize('')
        setNewCompForm({ name: '', materials: [{ materialName: '', proportion: '' }] })
        setShowNewCompForm(false)
        setShowVersionForm(true)
    }

    const handleCreateVersion = async (e) => {
        e.preventDefault()
        setCreatingVersion(true)
        try {
            const data = await apiFetch(`/product/${selected.product._id}/version`, {
                method: 'POST',
                body: JSON.stringify({
                    versionNumber: versionForm.versionNumber,
                    color: versionForm.color,
                    additionalPrice: parseFloat(versionForm.additionalPrice) || 0,
                    sizeId: versionForm.sizeId,
                    compositionId: versionForm.compositionId,
                }),
            })
            // Re-fetch detail to get populated version (compositionId/sizeId populated)
            const refreshed = await apiFetch(`/product/${selected.product._id}`)
            setSelected(refreshed)
            setShowVersionForm(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setCreatingVersion(false)
        }
    }

    const handleDeleteVersion = async (versionId) => {
        if (!window.confirm('Supprimer cette version ?')) return
        setDeletingVersionId(versionId)
        try {
            await apiFetch(`/product/version/${versionId}`, { method: 'DELETE' })
            setSelected(prev => ({ ...prev, versions: prev.versions.filter(v => v._id !== versionId) }))
        } catch (err) {
            alert(err.message)
        } finally {
            setDeletingVersionId(null)
        }
    }

    const handleToggleOnline = async (versionId, currentStatus) => {
        try {
            const data = await apiFetch(`/product/version/${versionId}/online`, {
                method: 'PATCH',
                body: JSON.stringify({ onlineStatus: !currentStatus }),
            })
            setSelected(prev => ({
                ...prev,
                versions: prev.versions.map(v => v._id === versionId ? data.productVersion : v),
            }))
            if (publishStatus?.published) {
                await apiFetch(`/product/${selected.product._id}/publish`, { method: 'POST' })
            }
        } catch (err) {
            alert(err.message)
        }
    }

    const handleAddSize = async () => {
        if (!newSize.trim()) return
        setAddingSize(true)
        try {
            const data = await apiFetch('/size', {
                method: 'POST',
                body: JSON.stringify({ label: newSize.trim() }),
            })
            setSizes(prev => [...prev, data.size])
            setVersionForm(prev => ({ ...prev, sizeId: data.size._id }))
            setNewSize('')
        } catch (err) {
            alert(err.message)
        } finally {
            setAddingSize(false)
        }
    }

    const handleAddComposition = async () => {
        const name = newCompForm.name.trim()
        if (!name) return
        const materials = newCompForm.materials
            .filter(m => m.materialName.trim())
            .map(m => ({ materialName: m.materialName.trim(), proportion: parseFloat(m.proportion) || 0 }))
        setAddingComp(true)
        try {
            const data = await apiFetch('/composition', {
                method: 'POST',
                body: JSON.stringify({ name, materials }),
            })
            setCompositions(prev => [...prev, data.composition])
            setVersionForm(prev => ({ ...prev, compositionId: data.composition._id }))
            setNewCompForm({ name: '', materials: [{ materialName: '', proportion: '' }] })
            setShowNewCompForm(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setAddingComp(false)
        }
    }

    const addMaterialRow = () =>
        setNewCompForm(prev => ({ ...prev, materials: [...prev.materials, { materialName: '', proportion: '' }] }))

    const removeMaterialRow = (i) =>
        setNewCompForm(prev => ({ ...prev, materials: prev.materials.filter((_, idx) => idx !== i) }))

    const updateMaterial = (i, field, value) =>
        setNewCompForm(prev => ({
            ...prev,
            materials: prev.materials.map((m, idx) => idx === i ? { ...m, [field]: value } : m),
        }))

    // ── Upload image via wmedia ───────────────────────────────────────────────

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        // Réinitialiser l'input pour permettre le re-upload du même fichier
        e.target.value = ''

        setUploadingImage(true)
        try {
            // 1. Init — obtenir la presigned URL
            setUploadProgress('Initialisation...')
            const initData = await apiFetch('/files/upload/init', {
                method: 'POST',
                body: JSON.stringify({ contentType: file.type, contentLength: file.size, filename: file.name }),
            })

            // 2. PUT direct vers S3/MinIO — PAS de credentials (requête externe)
            setUploadProgress('Envoi du fichier...')
            const putRes = await fetch(initData.presignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file,
            })
            if (!putRes.ok) throw new Error(`Erreur PUT S3 : ${putRes.status}`)

            // 3. Confirm
            setUploadProgress('Traitement...')
            const confirmData = await apiFetch('/files/upload/confirm', {
                method: 'POST',
                body: JSON.stringify({ uploadId: initData.uploadId, contentType: file.type }),
            })
            const mediaId = confirmData.id

            // 4. Poll jusqu'à status=ready (max 30s)
            let mediaUrl = null
            for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 1000))
                const statusData = await apiFetch(`/files/upload/status/${mediaId}`)
                if (statusData.status === 'ready') {
                    mediaUrl = statusData.urls?.md || statusData.urls?.original
                    break
                }
                if (statusData.status === 'error') throw new Error('Erreur de traitement wmedia')
            }
            if (!mediaUrl) throw new Error('Timeout : traitement wmedia trop long')

            // 5. Ajouter l'URL au produit
            setUploadProgress('Enregistrement...')
            const newImages = [...(selected.product.images || []), mediaUrl]
            const data = await apiFetch(`/product/${selected.product._id}`, {
                method: 'PUT',
                body: JSON.stringify({ images: newImages }),
            })
            setSelected(prev => ({ ...prev, product: data.product }))
            setProducts(prev => prev.map(p => p._id === data.product._id ? data.product : p))
            if (publishStatus?.published) {
                await apiFetch(`/product/${selected.product._id}/publish`, { method: 'POST' })
            }
        } catch (err) {
            alert(err.message)
        } finally {
            setUploadingImage(false)
            setUploadProgress('')
        }
    }

    const handleRemoveImage = async (url) => {
        const newImages = (selected.product.images || []).filter(u => u !== url)
        try {
            const data = await apiFetch(`/product/${selected.product._id}`, {
                method: 'PUT',
                body: JSON.stringify({ images: newImages }),
            })
            setSelected(prev => ({ ...prev, product: data.product }))
            if (publishStatus?.published) {
                await apiFetch(`/product/${selected.product._id}/publish`, { method: 'POST' })
            }
        } catch (err) {
            alert(err.message)
        }
    }

    // ── DETAIL VIEW ───────────────────────────────────────────────────────────

    if (detailLoading) return <div className="products-loading">Chargement...</div>

    if (selected) {
        const { product, versions } = selected
        return (
            <div className="products-detail">
                <div className="products-detail-header">
                    <button className="products-back-btn" onClick={() => setSelected(null)}>← Retour</button>
                    <h2 className="products-detail-title">{product.title}</h2>
                    {publishStatus !== null && (
                        publishStatus.published ? (
                            <button className="products-btn-published" onClick={handleUnpublish} disabled={publishing}>
                                {publishing ? '...' : '✓ Publié — Dépublier'}
                            </button>
                        ) : (
                            <button className="products-btn-publish" onClick={handlePublish} disabled={publishing}>
                                {publishing ? 'Publication...' : '↑ Publier dans Wink'}
                            </button>
                        )
                    )}
                    <button
                        className="products-edit-btn"
                        onClick={() => {
                            setEditMode(e => !e)
                            setEditForm({ title: product.title, price: product.price, description: product.description || '' })
                        }}
                    >
                        {editMode ? 'Annuler' : 'Modifier'}
                    </button>
                </div>

                {editMode ? (
                    <form className="products-edit-form" onSubmit={handleSaveEdit}>
                        <div className="products-field">
                            <label>Nom du produit</label>
                            <input
                                value={editForm.title}
                                onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="products-field">
                            <label>Prix (€)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={editForm.price}
                                onChange={e => setEditForm(p => ({ ...p, price: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="products-field">
                            <label>Description</label>
                            <textarea
                                value={editForm.description}
                                onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                                rows={3}
                            />
                        </div>
                        <div className="products-form-actions">
                            <button type="button" className="products-btn-outline" onClick={() => setEditMode(false)}>Annuler</button>
                            <button type="submit" className="products-btn-primary" disabled={saving}>
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="products-info-card">
                        <div className="products-info-row">
                            <span className="products-info-label">Prix</span>
                            <span>{product.price} €</span>
                        </div>
                        {product.description && (
                            <div className="products-info-row">
                                <span className="products-info-label">Description</span>
                                <span>{product.description}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── IMAGES ───────────────────────────────────────────────── */}
                <div className="products-images-section">
                    <div className="products-versions-header">
                        <h3>Images ({(selected.product.images || []).length})</h3>
                        <label className={`products-btn-primary products-upload-label${uploadingImage ? ' disabled' : ''}`}>
                            {uploadingImage ? uploadProgress || 'Upload...' : '+ Ajouter une image'}
                            <input
                                type="file" accept="image/*" style={{ display: 'none' }}
                                onChange={handleImageUpload} disabled={uploadingImage}
                            />
                        </label>
                    </div>
                    {(selected.product.images || []).length > 0 && (
                        <div className="products-images-grid">
                            {selected.product.images.map((url, i) => (
                                <div key={i} className="products-image-card">
                                    <img src={url} alt={`img-${i}`} className="products-image-thumb" />
                                    <button
                                        className="products-image-remove"
                                        onClick={() => handleRemoveImage(url)}
                                        title="Supprimer"
                                    >✕</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── VERSIONS ─────────────────────────────────────────────── */}
                <div className="products-versions-section">
                    <div className="products-versions-header">
                        <h3>Versions ({versions.length})</h3>
                        {!showVersionForm && (
                            <button className="products-btn-primary" onClick={openVersionForm}>
                                + Nouvelle version
                            </button>
                        )}
                    </div>

                    {showVersionForm && (
                        <form className="products-version-form" onSubmit={handleCreateVersion}>
                            <h4>Nouvelle version</h4>

                            <div className="products-field">
                                <label>Numéro de version</label>
                                <input
                                    value={versionForm.versionNumber}
                                    onChange={e => setVersionForm(p => ({ ...p, versionNumber: e.target.value }))}
                                    required placeholder="ex : 1"
                                />
                            </div>

                            <div className="products-field">
                                <label>Couleur</label>
                                <input
                                    value={versionForm.color}
                                    onChange={e => setVersionForm(p => ({ ...p, color: e.target.value }))}
                                    required placeholder="ex : Rouge ou #FF0000"
                                />
                            </div>

                            <div className="products-field">
                                <label>Prix supplémentaire (€)</label>
                                <input
                                    type="number" step="0.01" min="0"
                                    value={versionForm.additionalPrice}
                                    onChange={e => setVersionForm(p => ({ ...p, additionalPrice: e.target.value }))}
                                />
                            </div>

                            <div className="products-field">
                                <label>Taille</label>
                                <div className="products-select-with-add">
                                    <select
                                        value={versionForm.sizeId}
                                        onChange={e => setVersionForm(p => ({ ...p, sizeId: e.target.value }))}
                                        required
                                    >
                                        <option value="">— Choisir —</option>
                                        {sizes.map(s => <option key={s._id} value={s._id}>{s.label}</option>)}
                                    </select>
                                    <input
                                        className="products-inline-input"
                                        value={newSize}
                                        onChange={e => setNewSize(e.target.value)}
                                        placeholder="Nouvelle taille"
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddSize())}
                                    />
                                    <button
                                        type="button" className="products-btn-sm"
                                        onClick={handleAddSize}
                                        disabled={addingSize || !newSize.trim()}
                                    >+</button>
                                </div>
                            </div>

                            <div className="products-field">
                                <label>Composition</label>
                                <div className="products-select-with-add">
                                    <select
                                        value={versionForm.compositionId}
                                        onChange={e => setVersionForm(p => ({ ...p, compositionId: e.target.value }))}
                                        required
                                    >
                                        <option value="">— Choisir —</option>
                                        {compositions.map(c => (
                                            <option key={c._id} value={c._id}>
                                                {c.name}{c.materials?.length > 0 ? ` (${c.materials.map(m => `${m.materialName} ${m.proportion}%`).join(', ')})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button" className="products-btn-sm"
                                        onClick={() => setShowNewCompForm(v => !v)}
                                    >{showNewCompForm ? '−' : '+ Nouvelle'}</button>
                                </div>
                                {showNewCompForm && (
                                    <div className="products-comp-form">
                                        <input
                                            className="products-inline-input"
                                            value={newCompForm.name}
                                            onChange={e => setNewCompForm(p => ({ ...p, name: e.target.value }))}
                                            placeholder="Nom (ex : Coton bio)"
                                        />
                                        <div className="products-comp-materials">
                                            {newCompForm.materials.map((m, i) => (
                                                <div key={i} className="products-comp-material-row">
                                                    <input
                                                        className="products-inline-input"
                                                        value={m.materialName}
                                                        onChange={e => updateMaterial(i, 'materialName', e.target.value)}
                                                        placeholder="Matière (ex : Coton)"
                                                        style={{ flex: 2 }}
                                                    />
                                                    <input
                                                        className="products-inline-input"
                                                        type="number" min="0" max="100" step="0.1"
                                                        value={m.proportion}
                                                        onChange={e => updateMaterial(i, 'proportion', e.target.value)}
                                                        placeholder="%"
                                                        style={{ flex: 1, width: 60 }}
                                                    />
                                                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>%</span>
                                                    {newCompForm.materials.length > 1 && (
                                                        <button type="button" className="products-btn-sm" style={{ background: 'rgba(255,80,80,0.2)' }} onClick={() => removeMaterialRow(i)}>✕</button>
                                                    )}
                                                </div>
                                            ))}
                                            <button type="button" className="products-btn-sm" onClick={addMaterialRow}>+ Matière</button>
                                        </div>
                                        <button
                                            type="button" className="products-btn-primary"
                                            onClick={handleAddComposition}
                                            disabled={addingComp || !newCompForm.name.trim()}
                                            style={{ marginTop: 6, alignSelf: 'flex-start' }}
                                        >{addingComp ? '...' : 'Créer la composition'}</button>
                                    </div>
                                )}
                            </div>

                            <div className="products-form-actions">
                                <button type="button" className="products-btn-outline" onClick={() => setShowVersionForm(false)}>
                                    Annuler
                                </button>
                                <button type="submit" className="products-btn-primary" disabled={creatingVersion}>
                                    {creatingVersion ? 'Création...' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    )}

                    {versions.length === 0 ? (
                        <p className="products-empty-text">Aucune version. Créez la première version de ce produit.</p>
                    ) : (
                        <div className="products-versions-list">
                            {versions.map(v => (
                                <div key={v._id} className="products-version-card">
                                    <div className="products-version-info">
                                        <span className="products-version-number">v{v.versionNumber}</span>
                                        <span className="products-version-color">
                                            {/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v.color) && (
                                                <span className="products-version-color-dot" style={{ background: v.color }} />
                                            )}
                                            {v.color}
                                        </span>
                                        <span>{v.sizeId?.label || '—'}</span>
                                        <span className="products-version-comp">
                                            {v.compositionId
                                                ? (v.compositionId.materials?.length > 0
                                                    ? v.compositionId.materials.map(m => `${m.materialName} ${m.proportion}%`).join(', ')
                                                    : v.compositionId.name)
                                                : '—'}
                                        </span>
                                        {v.additionalPrice > 0 && <span>+{v.additionalPrice} €</span>}
                                    </div>
                                    <div className="products-version-actions">
                                        <button
                                            className={`products-toggle ${v.onlineStatus ? 'online' : 'offline'}`}
                                            onClick={() => handleToggleOnline(v._id, v.onlineStatus)}
                                        >
                                            {v.onlineStatus ? 'En ligne' : 'Hors ligne'}
                                        </button>
                                        <button
                                            className="products-btn-danger"
                                            onClick={() => handleDeleteVersion(v._id)}
                                            disabled={deletingVersionId === v._id}
                                        >
                                            {deletingVersionId === v._id ? '...' : '✕'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="products-danger-zone">
                    <button
                        className="products-btn-danger-outline"
                        onClick={() => handleDeleteProduct(product._id)}
                        disabled={deletingProductId === product._id}
                    >
                        {deletingProductId === product._id ? 'Suppression...' : 'Supprimer ce produit'}
                    </button>
                </div>
            </div>
        )
    }

    // ── LIST VIEW ─────────────────────────────────────────────────────────────

    if (loading) return <div className="products-loading">Chargement...</div>

    return (
        <div className="products-page">
            <div className="products-header">
                <h2>Mes produits</h2>
                <button
                    className="products-btn-primary"
                    onClick={() => { setShowCreate(s => !s); setCreateForm(EMPTY_CREATE_FORM) }}
                >
                    {showCreate ? 'Annuler' : '+ Nouveau produit'}
                </button>
            </div>

            {showCreate && (
                <form className="products-create-form" onSubmit={handleCreate}>
                    <div className="products-field">
                        <label>Nom du produit</label>
                        <input
                            value={createForm.title}
                            onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))}
                            required placeholder="Nom"
                        />
                    </div>
                    <div className="products-field">
                        <label>Prix (€)</label>
                        <input
                            type="number" step="0.01" min="0"
                            value={createForm.price}
                            onChange={e => setCreateForm(p => ({ ...p, price: e.target.value }))}
                            required placeholder="0.00"
                        />
                    </div>
                    <div className="products-field">
                        <label>Description (optionnel)</label>
                        <textarea
                            value={createForm.description}
                            onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                            rows={2} placeholder="Description du produit"
                        />
                    </div>
                    <div className="products-form-actions">
                        <button type="submit" className="products-btn-primary" disabled={creating}>
                            {creating ? 'Création...' : 'Créer'}
                        </button>
                    </div>
                </form>
            )}

            {products.length === 0 ? (
                <p className="products-empty-text">Aucun produit. Créez votre premier produit !</p>
            ) : (
                <div className="products-list">
                    {products.map(p => (
                        <div key={p._id} className="products-list-item" onClick={() => selectProduct(p._id)}>
                            <div className="products-list-item-info">
                                <span className="products-list-item-title">{p.title}</span>
                                <span className="products-list-item-price">{p.price} €</span>
                            </div>
                            <span className="products-list-item-arrow">›</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default Products
